import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Badge, Button, Modal, Table} from 'react-bootstrap';
import Visibility from '@mui/icons-material/Visibility';
import {TableSkeleton} from '../../../../shared/ui/LoadingSkeletons';
import {fetchAggregatedLogs} from '../../services/influxApi';
import {useChartConfig, useTimeRange} from '../../context/StatisticsContext';
import '../../../edges/styles/edgeTable.css';

const limit = 20;

const AggregatedLogsChart = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const {start, end} = useTimeRange();
  const {edgeId} = useChartConfig();

  const getLogs = useCallback(async () => {
    if (!edgeId || !start || !end) return;

    setLoading(true);
    const result = await fetchAggregatedLogs(edgeId, start, end, limit);

    if (!result.ok) {
      console.error('Error fetching logs:', result.error);
      setData([]);
    } else {
      setData(result.data || []);
    }
    setLoading(false);
  }, [edgeId, start, end]);

  useEffect(() => {
    getLogs();
  }, [getLogs]);

  const handleShow = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedLog(null);
  };

  const buildTagsString = (d) => {
    const tags = [];
    for (let i = 0; i < 10; i++) {
      if (d[`tags_${i}`]) {
        tags.push(d[`tags_${i}`]);
      }
    }
    return tags.join(' / ');
  };

  return (
    <div>
      {loading ? (
        <TableSkeleton rows={5} columns={4}/>
      ) : (
        <>
          {data.length === 0 ? (
            <Alert variant="light">No logs available for the selected time period</Alert>
          ) : (
            <div className="table-responsive rounded shadow-sm">
              <Table className="align-middle mb-0 rounded overflow-hidden" hover>
                <thead>
                <tr>
                  <th style={{width: '20px'}}>Level</th>
                  <th style={{width: '40px'}}>Time</th>
                  <th style={{width: '20%', whiteSpace: 'normal'}}>Tags</th>
                  <th style={{whiteSpace: 'normal'}}>Message</th>
                  <th style={{width: '0px'}}>Details</th>
                </tr>
                </thead>
                <tbody style={{fontSize: '0.875rem'}}>
                {data.map((d, i) => (
                  <tr key={i}>
                    <td style={{verticalAlign: 'middle'}}>
                      <Badge pill bg={d.level === 'debug' ? 'info' : d.level === 'log' ? 'warning' : 'danger'}>
                        {d.level}
                      </Badge>
                    </td>
                    <td style={{fontSize: '0.75rem'}}>{new Date(d._time).toLocaleString()}</td>
                    <td style={{whiteSpace: 'normal', fontSize: '0.75rem'}}>{buildTagsString(d)}</td>
                    <td style={{whiteSpace: 'normal'}}>{d._value}</td>
                    <td>
                      <Button variant="link" size="sm" onClick={() => handleShow(d)} style={{padding: 0}}>
                        <Visibility fontSize="small"/>
                      </Button>
                    </td>
                  </tr>
                ))}
                </tbody>
              </Table>
            </div>
          )}

          <Modal show={showModal} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Log Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedLog && (
                <div>
                  <p><strong>Level:</strong> {selectedLog.level}</p>
                  <p><strong>Time:</strong> {new Date(selectedLog._time).toLocaleString()}</p>
                  <p><strong>Tags:</strong> {buildTagsString(selectedLog)}</p>
                  <p><strong>Message:</strong></p>
                  <pre style={{whiteSpace: 'pre-wrap', wordWrap: 'break-word'}}>{selectedLog._value}</pre>
                </div>
              )}
            </Modal.Body>
          </Modal>
        </>
      )}
    </div>
  );
};

export default AggregatedLogsChart;

